import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

interface ScamReport {
  id: number;
  site_name: string;
  domain: string;
  status: string;
}

export default function VerificationReportPage() {
  const { user } = useAuth();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [recentReports, setRecentReports] = useState<ScamReport[]>([]);
  const [formData, setFormData] = useState({ siteName: "", domain: "", issue: "", description: "" });

  const fetchRecentReports = useCallback(async () => {
    const { data } = await supabase
      .from("scam_reports")
      .select("id, site_name, domain, status")
      .order("created_at", { ascending: false })
      .limit(4);

    if (data) setRecentReports(data);
  }, []);

  useEffect(() => {
    fetchRecentReports();
  }, [fetchRecentReports]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    const form = e.currentTarget;
    const honeypot = form.elements.namedItem("website_alt") as HTMLInputElement;
    if (honeypot && honeypot.value.trim()) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const { error: insertError } = await supabase.from("scam_reports").insert({
        site_name: formData.siteName.trim(),
        domain: formData.domain.trim(),
        issue_type: formData.issue,
        description: formData.description.trim(),
        author_id: user?.id || null,
      });

      if (insertError) {
        setError("제보 접수 중 오류가 발생했습니다. 다시 시도해주세요.");
        return;
      }

      setSubmitted(true);
      setFormData({ siteName: "", domain: "", issue: "", description: "" });
      fetchRecentReports();
    } catch {
      setError("제보 접수 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      {/* Page Header */}
      <section className="bg-background-100 border-b border-background-300">
        <div className="max-w-[860px] mx-auto px-3 md:px-4 py-8 md:py-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-400 transition-colors mb-3"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            홈으로
          </Link>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 flex items-center justify-center text-red-400">
              <i className="ri-alert-line text-2xl" />
            </span>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-700">먹튀 제보하기</h1>
              <p className="text-sm text-foreground-600 mt-0.5">
                피해 사례를 제보해주시면 신속하게 검증 후 공유하겠습니다
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-[860px] mx-auto px-3 md:px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Report Form */}
          <div className="md:col-span-3 bg-background-100 rounded-lg border border-background-300 p-5 md:p-6">
            <h2 className="text-base font-bold text-foreground-700 mb-4 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-red-400">
                <i className="ri-error-warning-line" />
              </span>
              피해 사이트 정보 입력
            </h2>
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-primary-500/10 flex items-center justify-center">
                  <span className="w-7 h-7 flex items-center justify-center text-primary-500">
                    <i className="ri-check-line text-2xl" />
                  </span>
                </div>
                <h3 className="text-base font-bold text-foreground-700 mb-1">제보가 접수되었습니다</h3>
                <p className="text-sm text-foreground-600 mb-4">검증팀에서 확인 후 신속하게 처리하겠습니다</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-4 py-2 text-sm bg-primary-500 text-foreground-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  추가 제보하기
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-400">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold text-foreground-600 mb-1.5">사이트명</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.siteName}
                    onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
                    placeholder="예: 럭키존"
                    className="w-full bg-background-200 rounded-lg px-4 py-2.5 text-sm text-foreground-700 border border-background-300 focus:border-red-400/50 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-foreground-600 mb-1.5">도메인</label>
                  <input
                    type="text"
                    name="domain"
                    required
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    placeholder="예: lucky-zone.net"
                    className="w-full bg-background-200 rounded-lg px-4 py-2.5 text-sm text-foreground-700 border border-background-300 focus:border-red-400/50 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-foreground-600 mb-1.5">피해 유형</label>
                  <select
                    name="inquiry_type"
                    value={formData.issue}
                    onChange={(e) => setFormData({ ...formData, issue: e.target.value })}
                    className="w-full bg-background-200 rounded-lg px-4 py-2.5 text-sm text-foreground-700 border border-background-300 focus:border-red-400/50 outline-none transition-colors"
                  >
                    <option value="">선택해주세요</option>
                    <option value="환전 거부">환전 거부</option>
                    <option value="사이트 폐쇄">사이트 폐쇄</option>
                    <option value="추가 입금 요구">추가 입금 요구</option>
                    <option value="잔액 몰수">잔액 몰수</option>
                    <option value="환전 지연">환전 지연</option>
                    <option value="기타">기타</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-foreground-600 mb-1.5">상세 내용</label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="구체적인 피해 상황을 설명해주세요"
                    className="w-full bg-background-200 rounded-lg px-4 py-2.5 text-sm text-foreground-700 border border-background-300 focus:border-red-400/50 outline-none transition-colors resize-none"
                  />
                </div>

                <div className="hp-field">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2.5 bg-red-400 hover:bg-red-500 text-foreground-50 text-sm font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {submitting ? "접수 중..." : "제보하기"}
                </button>
              </form>
            )}
          </div>

          {/* Recent Reports + Info */}
          <div className="md:col-span-2 space-y-4">
            <div className="bg-background-100 rounded-lg border border-background-300 p-5">
              <h3 className="text-sm font-bold text-foreground-700 mb-3">최근 제보 현황</h3>
              <div className="space-y-2">
                {recentReports.length === 0 ? (
                  <p className="text-xs text-foreground-600 py-4 text-center">아직 접수된 제보가 없습니다</p>
                ) : (
                  recentReports.map((report) => (
                    <div key={report.id} className="flex items-center justify-between text-xs py-2 border-b border-background-300/50 last:border-0">
                      <div className="min-w-0">
                        <span className="text-foreground-700 font-medium">{report.site_name}</span>
                        <span className="text-foreground-600 ml-2">{report.domain}</span>
                      </div>
                      <span
                        className={`flex-shrink-0 px-1.5 py-0.5 text-[10px] rounded ${
                          report.status === "확인완료"
                            ? "bg-accent-500/10 text-accent-500"
                            : "bg-secondary-500/10 text-secondary-400"
                        }`}
                      >
                        {report.status}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="bg-background-100 rounded-lg border border-background-300 p-5">
              <h3 className="text-sm font-bold text-foreground-700 mb-3">제보 시 유의사항</h3>
              <ul className="space-y-2 text-xs text-foreground-600">
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-500 flex-shrink-0">
                    <i className="ri-checkbox-circle-line" />
                  </span>
                  정확한 사이트명과 도메인을 기재해주세요
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-500 flex-shrink-0">
                    <i className="ri-checkbox-circle-line" />
                  </span>
                  입출금 내역 등 증빙자료가 있다면 상세 내용에 포함해주세요
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-500 flex-shrink-0">
                    <i className="ri-checkbox-circle-line" />
                  </span>
                  허위 제보 시 서비스 이용이 제한될 수 있습니다
                </li>
              </ul>
            </div>

            <Link
              to="/verification"
              className="block bg-background-100 rounded-lg border border-background-300 p-4 text-center hover:border-primary-500/30 transition-colors"
            >
              <span className="text-sm text-foreground-600">검증 프로세스 자세히 보기</span>
              <span className="w-4 h-4 inline-flex items-center justify-center text-primary-500 ml-1">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}